/** @jsxImportSource @emotion/react */

import PropTypes from 'prop-types';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { DISABILITY_ID, EMPTY_STRING } from '../../const';
import { useOnboardingDataContext } from '../../context/OnboardingData';
import getNextQuestionId from '../../utils/getNextQuestionId';
import ButtonAnswer from '../ButtonAnswer';
import { ErrorIcon } from '../Icons';
import Text from '../Text';
import useStyles from './styles';

const getSelectedQuestion = () => {
  switch (true) {
    case JSON.parse(sessionStorage.getItem('ISLETGOGIRLSPLAY')):
      return DISABILITY_ID;
    case JSON.parse(sessionStorage.getItem('ISJUSTPLAY')):
      return 'd9572be5-72f2-42af-87ea-a3298c6eacd7';
    case JSON.parse(sessionStorage.getItem('ISVOLUNTEER')):
      return 'd9572be5-72f2-42af-87ea-a3298c6eacd7';
    case JSON.parse(sessionStorage.getItem('ISWILDCATS')):
      return DISABILITY_ID;
    case JSON.parse(sessionStorage.getItem('ISYOUTHCLUB')):
      return '346dbfab-03fa-4a3a-ab18-b461d0d56dc8';
    default:
      return;
  }
};

const AgeQuestion = React.forwardRef(
  (
    {
      readableId,
      readableIdYear,
      analyticsId,
      inputTitle,
      ageRanges,
      onAnswer,
      headerTitle,
      yearGroup,
      onClickFunction,
      nextClickAnswer,
      questionOrder,
      ...rest
    },
    answerRef,
  ) => {
    const styles = useStyles();
    const [age, setAge] = useState('');
    const [isTouched, setIsTouched] = useState(false);
    const validationError = isTouched && (age === '' || Number(age) < 5);
    const inputRef = useRef();
    const [selectedAnswer, setSelectedAnswer] = useState('');
    const isMini = sessionStorage.getItem('ISMINIONBOARDING') === 'true';
    const {
      OnboardingContext,
      setOnboardingDataContext,
      arrowContext,
      setArrowFunction,
      setSelectedAgeFunction,
    } = useOnboardingDataContext();

    useEffect(() => {
      setOnboardingDataContext({
        ...OnboardingContext,
        question: headerTitle,
        step: 1,
        age: undefined,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [headerTitle]);

    const selectedYear = yearGroup.filter(
      item => item.age === parseInt(inputRef?.current?.value, 10),
    )[0]?.options;

    const selectAnswer = answer => {
      if (selectedAnswer === answer) {
        setSelectedAnswer('');
        setArrowFunction(false);
      } else {
        setSelectedAnswer(answer);
        setArrowFunction(true);
      }
    };

    const handleNextNav = useCallback(
      (
        age,
        readableId,
        ageRanges,
        onAnswer,
        selectedAnswer,
        selectedYear,
        checkingdate,
      ) => {
        setTimeout(() => {
          const today = new Date();
          const currentyear = today.getFullYear();
          //sep 1st to dec 31st
          const olderstartdate = new Date(`09/01/${currentyear}`);
          const olderenddate = new Date(`12/31/${currentyear}`);
          //jan 1st to aug 31st
          const youngerstartdate = new Date(`01/01/${currentyear}`);
          const youngerenddate = new Date(`08/31/${currentyear}`);

          const selectedQuestion = getSelectedQuestion();
          // if no error, proceed to the next question.

          onAnswer({
            answerData: {
              [readableId]: age,
              [!isMini && readableIdYear]:
                !isMini && age >= 19
                  ? ''
                  : selectedAnswer === 'Other year group'
                    ? today >= olderstartdate && today <= olderenddate
                      ? selectedYear[1]
                      : today >= youngerstartdate && today <= youngerenddate
                        ? selectedYear[0]
                        : EMPTY_STRING
                    : selectedAnswer,
            },
            nextQuestionId: getNextQuestionId({
              ageRanges,
              selectedAge: age,
              selectedQuestion,
            }),
          });
        }, 500);
      },
      [readableIdYear, isMini],
    );

    useImperativeHandle(answerRef, () => ({
      handleNextNav,
    }));

    let isMouseDown = false; // Flag to track mouse interaction
    const pageRestoredRef = useRef(false);

    const handleMouseDown = () => {
      isMouseDown = true; // Set the flag to true on mouse down
      pageRestoredRef.current = false; // Reset on user interaction
    };

    const handleFocus = e => {
      inputRef.current.placeholder = '';
      // Don't apply focus styling if page was restored from background
      if (pageRestoredRef.current) {
        pageRestoredRef.current = false; // Reset flag
        inputRef.current.classList.remove('keyboard-focus', 'mouse-focus');
        return;
      }
      if (isMouseDown) {
        // Focus triggered by mouse
        inputRef.current.classList.add('mouse-focus');
        inputRef.current.classList.remove('keyboard-focus');
      } else {
        // Focus triggered by keyboard (e.g., Tab key)
        inputRef.current.classList.add('keyboard-focus');
        inputRef.current.classList.remove('mouse-focus');
      }
      isMouseDown = false; // Reset the flag after focus
    };

    const handleBlur = () => {
      inputRef.current.classList.remove('keyboard-focus', 'mouse-focus');
      inputRef.current.placeholder = "Type the players' age here";
      if (inputRef.current.value === '') {
        setIsTouched(true);
      }
    };

    //when user switches tabs
    useEffect(() => {
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          pageRestoredRef.current = true; // Set the flag when page is restored
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange,
        );
      };
    }, []);

    return (
      <Fragment>
        <Text as="div" css={styles.base(validationError)}>
          <div css={styles.inputWrapper}>
            <input
              type="text"
              className="user-age-input"
              id={analyticsId}
              placeholder="Type the players’ age here"
              ref={inputRef}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onMouseDown={handleMouseDown}
              minLength="1"
              maxLength="2"
              onChange={event => {
                const newVal = event.target.value;
                if (!isTouched) {
                  setIsTouched(true);
                }
                setArrowFunction(
                  !isMini
                    ? newVal >= 19
                      ? true
                      : selectedAnswer
                        ? true
                        : false
                    : true,
                  newVal,
                );
                setAge(newVal);
                setOnboardingDataContext({
                  ...OnboardingContext,
                  age: event.target.value,
                });
                setSelectedAgeFunction(event.target.value);
              }}
              onKeyPress={event => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
            />
            {validationError && (
              <div css={styles.errorMessage(validationError)}>
                <ErrorIcon /> <p>Please enter an age between 5 and 99</p>
              </div>
            )}
          </div>
          {selectedYear &&
            sessionStorage.getItem('ISMINIONBOARDING') !== 'true' && (
              <Text css={styles.helpText}>Select school year</Text>
            )}
          <ul role="none">
            {selectedYear &&
              sessionStorage.getItem('ISMINIONBOARDING') !== 'true' &&
              selectedYear.map((answer, index) => {
                const isSelected = selectedAnswer === answer ? true : false;
                return (
                  <li key={index} css={styles.answer}>
                    <label>
                      <ButtonAnswer
                        id={analyticsId}
                        tabIndex={0}
                        css={styles.ageYear(isSelected)}
                        onClick={() => {
                          selectAnswer(answer);
                        }}
                        {...rest}
                      >
                        {answer}
                      </ButtonAnswer>
                    </label>
                  </li>
                );
              })}
          </ul>

          <button
            id={`question_${questionOrder}_next`}
            aria-label="Next"
            css={styles.buttonNext(arrowContext, selectedYear, isMini)}
            disabled={
              validationError ||
              (isMini
                ? !age
                : !selectedAnswer
                  ? age >= 19
                    ? false
                    : true
                  : false)
            }
            onClick={() => {
              arrowContext === true && nextClickAnswer();
              handleNextNav(
                age,
                readableId,
                ageRanges,
                onAnswer,
                selectedAnswer,
                selectedYear,
              );
              setArrowFunction(false);
            }}
            tabIndex={0}
          >
            Next {!selectedAnswer ? true : false}
          </button>
          {/* 
          this code is for future reference :
          <Text as="div" css={styles.errorBox(validationError)} role="alert">
            {validationError && <p>{validationErrorMsg}</p>}
          </Text>
           */}
        </Text>
      </Fragment>
    );
  },
);
AgeQuestion.propTypes = {
  readableId: PropTypes.number,
  readableIdYear: PropTypes.number,
  analyticsId: PropTypes.number,
  inputTitle: PropTypes.string,
  ageRanges: PropTypes.string,
  onAnswer: PropTypes.string,
  headerTitle: PropTypes.string,
  yearGroup: PropTypes.string,
  onClickFunction: PropTypes.func,
  nextClickAnswer: PropTypes.func,
};
export default AgeQuestion;
